/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, forkJoin, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import * as jwt_decode from 'jwt-decode';

import { UserSettingsApiService } from './user-settings-api.service';
import {
  UserStateService,
  DEFAULT_FEATURE_FLAGS,
  DEFAULT_SCORELAB_SETTINGS,
  DEFAULT_ADMIN_SCORELAB_SETTINGS,
} from '../user-state.service';
import { PendoService } from '../pendo.service';
import { SentryService } from '../sentry.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { isKnownScoreType } from 'src/app/shared/utils/score-type-details';

interface UserContext {
  user: any;
  isAdmin: boolean;
  enableTopNews: boolean;
}

type OverrideRule =
  | { kind: 'scorelab'; src: string; dst: string }
  | { kind: 'scorelabValidatedScore'; src: string; dst: 'defaultScore' }
  | {
      kind: 'selfField';
      src: string;
      field: 'customDefaultPortfolio' | 'showArticleSentiment';
      fallback?: any;
    };

const OVERRIDE_RULES: OverrideRule[] = [
  {
    kind: 'selfField',
    src: 'customDefaultPortfolio',
    field: 'customDefaultPortfolio',
    fallback: '',
  },
  {
    kind: 'selfField',
    src: 'showArticleSentiment',
    field: 'showArticleSentiment',
  },
  { kind: 'scorelab', src: 'user_customScores', dst: 'customScores' },
  {
    kind: 'scorelabValidatedScore',
    src: 'user_defaultScore',
    dst: 'defaultScore',
  },
  { kind: 'scorelab', src: 'user_defaultFlag', dst: 'defaultFlag' },
  { kind: 'scorelab', src: 'user_customFlags', dst: 'customFlags' },
  { kind: 'scorelab', src: 'sentimentAccess', dst: 'sentimentAccess' },
  { kind: 'scorelab', src: 'generalRiskAccess', dst: 'generalRiskAccess' },
  { kind: 'scorelab', src: 'creditRiskAccess', dst: 'creditRiskAccess' },
  { kind: 'scorelab', src: 'esgRiskAccess', dst: 'esgRiskAccess' },
  { kind: 'scorelab', src: 'immediateRiskAccess', dst: 'immediateRiskAccess' },
  { kind: 'scorelab', src: 'esgAccess', dst: 'esgAccess' },
  { kind: 'scorelab', src: 'eb_simAccess', dst: 'eb_simAccess' },
  { kind: 'scorelab', src: 'bbAccess', dst: 'bbAccess' },
  { kind: 'scorelab', src: 'customScoreAccess', dst: 'customScoreAccess' },
];

const FALLBACK_PRIORITY: Array<{ access: string; pick: string }> = [
  { access: 'generalRiskAccess', pick: 'general_risk' },
  { access: 'creditRiskAccess', pick: 'credit_risk' },
  { access: 'esgRiskAccess', pick: 'esg_risk' },
  { access: 'immediateRiskAccess', pick: 'immediate_risk' },
  { access: 'esgAccess', pick: 'esg' },
  { access: 'eb_simAccess', pick: 'eb_sim' },
  { access: 'bbAccess', pick: 'bb' },
];

const DEFAULT_SCORE_ACCESS_FLAG: Record<string, string> = {
  sentiment: 'sentimentAccess',
  general_risk: 'generalRiskAccess',
  credit_risk: 'creditRiskAccess',
  esg_risk: 'esgRiskAccess',
  immediate_risk: 'immediateRiskAccess',
  esg: 'esgAccess',
  eb_sim: 'eb_simAccess',
  eb_sim_risk: 'eb_simAccess',
  bb: 'bbAccess',
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  cockpitSettings: any;
  customDefaultPortfolio: any;
  showArticleSentiment = false;

  private accountInfoSubject = new BehaviorSubject<any>(null);
  accountInfo$ = this.accountInfoSubject.asObservable();

  constructor(
    private userSettingsApi: UserSettingsApiService,
    private userState: UserStateService,
    private config: ConfigService,
    private routing: RoutingService,
    private pendoService: PendoService,
    private sentryService: SentryService,
  ) {}

  /**
   * Loads remote user-specific configuration after login. Runs as a single
   */
  load(token: string): Observable<void> {
    return forkJoin({
      cockpitSettings: this.userSettingsApi.getCustomSettings(token),
      accountInfo: this.userSettingsApi.getUserInfo(),
    }).pipe(
      tap(({ cockpitSettings, accountInfo }) => {
        const ctx = this.buildUserContext(token, accountInfo);
        this.applyBaseSettings(cockpitSettings, ctx);
        this.applyCockpitOverrides();
        this.reconcileDefaultScoreAccess();
        this.applyBrandingAndTheme();
        this.initPendoIfEligible(ctx);
        this.initSentryIfEligible(ctx);
      }),
      map(() => undefined),
      catchError((error) => this.handleError(error)),
    );
  }

  updateAccountInfo(accountInfo: any): void {
    this.accountInfoSubject.next(accountInfo);
  }

  private buildUserContext(token: string, accountInfo: any): UserContext {
    this.accountInfoSubject.next(JSON.parse(JSON.stringify(accountInfo)));
    const user: any = jwt_decode(token);
    const isAdmin =
      user?.roles?.includes('ADMIN') || user?.groups?.includes('ADMIN');
    const enableTopNews =
      user?.roles?.includes('ORG_INT') || user?.groups?.includes('ORG_INT');
    return { user, isAdmin, enableTopNews };
  }

  private applyBaseSettings(remote: any, ctx: UserContext): void {
    this.cockpitSettings = Object.prototype.hasOwnProperty.call(
      remote,
      'settings',
    )
      ? JSON.parse(JSON.stringify(remote['settings']))
      : {};
    Object.assign(
      this.userState.featureFlags,
      DEFAULT_FEATURE_FLAGS,
      this.cockpitSettings,
    );
    if (ctx.enableTopNews) {
      this.userState.featureFlags.showTopNews = true;
    }
    Object.assign(
      this.userState.scorelabSettings,
      ctx.isAdmin ? DEFAULT_ADMIN_SCORELAB_SETTINGS : DEFAULT_SCORELAB_SETTINGS,
    );
    if (ctx.isAdmin && this.cockpitSettings.betaLanguages !== false) {
      this.userState.featureFlags.betaLanguages = true;
    }
  }

  private applyCockpitOverrides(): void {
    for (const rule of OVERRIDE_RULES) {
      if (
        !Object.prototype.hasOwnProperty.call(this.cockpitSettings, rule.src)
      ) {
        if (rule.kind === 'selfField' && 'fallback' in rule) {
          this[rule.field] = rule.fallback;
        }
        continue;
      }

      const value = this.cockpitSettings[rule.src];

      switch (rule.kind) {
        case 'scorelab':
          this.userState.scorelabSettings[rule.dst] = value;
          break;
        case 'scorelabValidatedScore':
          if (!isKnownScoreType(value)) {
            this.cockpitSettings[rule.src] = 'sentiment';
          }
          this.userState.scorelabSettings[rule.dst] =
            this.cockpitSettings[rule.src];
          break;
        case 'selfField':
          this[rule.field] = JSON.parse(JSON.stringify(value));
          break;
      }
    }
  }

  private reconcileDefaultScoreAccess(): void {
    const s = this.userState.scorelabSettings;
    const denied = s.defaultScore;
    const accessKey = DEFAULT_SCORE_ACCESS_FLAG[denied];

    if (!accessKey) {
      // Unknown defaultScore: only act if customScoreAccess === false strictly.
      if (s.customScoreAccess !== false) return;
      this.pickFirstAvailable(s, FALLBACK_PRIORITY);
      if (s.defaultScore === denied) s.defaultScore = '';
      return;
    }

    if (s[accessKey] !== false) return;

    this.pickFirstAvailable(
      s,
      FALLBACK_PRIORITY.filter((fb) => fb.pick !== denied),
    );
    if (s.defaultScore === denied && s.customScoreAccess === true) {
      s.defaultScore = '';
    }
  }

  private pickFirstAvailable(
    s: any,
    fallbacks: Array<{ access: string; pick: string }>,
  ): void {
    for (const fb of fallbacks) {
      if (s[fb.access] === true) {
        s.defaultScore = fb.pick;
        return;
      }
    }
  }

  private applyBrandingAndTheme(): void {
    if (this.config.appConfig.customLogo) {
      this.userState.featureFlags.logo = this.config.appConfig.customLogo;
    }
    if (!this.routing.theme) {
      this.routing.inTheme(this.userState.featureFlags.theme);
    }
  }

  private initPendoIfEligible(ctx: UserContext): void {
    this.pendoService.initIfEligible(
      ctx.user,
      this.accountInfoSubject.getValue(),
      this.userState.scorelabSettings.defaultScore,
    );
  }

  private initSentryIfEligible(ctx: UserContext): void {
    this.sentryService.initIfEligible(ctx.user);
  }

  private handleError(error: HttpErrorResponse) {
    let message = 'Something bad happened please try again later.';
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
      message = error.error.message;
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`,
      );
      message = error.error;
    }
    return throwError(() => new Error(message));
  }
}
